import React, { useEffect, useMemo, useState } from "react";
import translations from "./lang";
import "./Notifications.css";

// API base: több környezeti változót is támogat
const API_BASE =
  process.env.REACT_APP_API ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

// Token-lekérő: több kulcs név támogatása
function getToken() {
  return (
    localStorage.getItem("access_token") || // leggyakoribb nálad
    localStorage.getItem("access") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    ""
  );
}

// Nyelv a localStorage-ból
function useLanguage() {
  return localStorage.getItem("language") || "hu";
}

// Idő formázás
function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Notifications() {
  const language = useLanguage();
  const t = translations[language] || translations.ro;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  // Fejlécek: Authorization + Accept-Language
  const headers = useMemo(() => {
    const token = getToken();
    const h = {
      "Content-Type": "application/json",
      "Accept-Language": language,
    };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [language]);

  // Fallback mock adatok
  const MOCK = [
    {
      id: "m1",
      title: "Foglalás megerősítve",
      message: "A 2025-09-09 14:00 időpontod megerősítve lett.",
      level: "success",
      created_at: new Date(Date.now() - 3600_000).toISOString(),
      read: false,
      link: "/bookings",
    },
    {
      id: "m2",
      title: "Új üzenet",
      message: "Új üzeneted érkezett az ügyféltől.",
      level: "info",
      created_at: new Date(Date.now() - 7200_000).toISOString(),
      read: true,
      link: "/bookings",
    },
    {
      id: "m3",
      title: "Időpont módosítva",
      message: "A 2025-09-10 10:00 időpont áthelyezve 11:00-ra.",
      level: "warning",
      created_at: new Date(Date.now() - 86400_000).toISOString(),
      read: false,
      link: "/AppointmentsTrai/Appointments",
    },
  ];

  // Lista lekérés
  async function fetchNotifications() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/notifications/`, { headers });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();

      // Rugalmas parse: [] vagy {data: []} vagy {results: []}
      const rows = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.results)
            ? json.results
            : [];

      setItems(rows);
    } catch (e) {
      console.warn("Értesítések betöltése sikertelen, mock adatokkal fut:", e);
      setItems(MOCK);
      setError(t.notifications_fallback || "A szerver nem elérhető – demó adatok láthatók.");
    } finally {
      setLoading(false);
    }
  }

  // Olvasottra / olvasatlanra
  async function markRead(id, read = true) {
    try {
      const url = read
        ? `${API_BASE}/api/notifications/${id}/mark_read/`
        : `${API_BASE}/api/notifications/${id}/mark_unread/`;
      const res = await fetch(url, { method: "PATCH", headers, body: "{}" });
      if (!res.ok) throw new Error();
      setItems(prev => prev.map(n => (n.id === id ? { ...n, read } : n)));
    } catch {
      // Optimista frissítés fallbackként
      setItems(prev => prev.map(n => (n.id === id ? { ...n, read } : n)));
    }
  }

  // Összes olvasott
  async function markAllRead() {
    try {
      const res = await fetch(`${API_BASE}/api/notifications/mark_all_read/`, {
        method: "PATCH",
        headers,
        body: "{}",
      });
      if (!res.ok) throw new Error();
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    }
  }

  // Törlés
  async function remove(id) {
    try {
      const res = await fetch(`${API_BASE}/api/notifications/${id}/`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error();
      setItems(prev => prev.filter(n => n.id !== id));
    } catch {
      setItems(prev => prev.filter(n => n.id !== id));
    }
  }

  // Betöltés + nyelvváltáskor újra
  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Kliens oldali szűrés/rendezés
  const filtered = useMemo(() => {
    return items
      .filter(n => {
        if (filter === "unread") return !n.read;
        if (filter === "read") return !!n.read;
        return true;
      })
      .filter(n => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
          String(n.title || "").toLowerCase().includes(q) ||
          String(n.message || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [items, filter, search]);

  const unreadCount = items.filter(n => !n.read).length;

  return (
    <div className="noti-wrap">
      <header className="noti-header">
        <div className="noti-title">
          <span>🔔</span>
          <h1>{t.notifications_title}</h1>
          {unreadCount > 0 && (
            <span className="noti-chip">
              {t.notifications_unread_chip.replace("{n}", unreadCount)}
            </span>
          )}
        </div>

        <div className="noti-actions">
          <div className="noti-field">
            <label htmlFor="noti-filter">{t.notifications_filter}</label>
            <select
              id="noti-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">{t.notifications_filter_all}</option>
              <option value="unread">{t.notifications_filter_unread}</option>
              <option value="read">{t.notifications_filter_read}</option>
            </select>
          </div>

          <div className="noti-field">
            <input
              type="search"
              placeholder={t.notifications_search_ph}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button className="noti-btn" onClick={fetchNotifications}>
            {t.notifications_refresh}
          </button>
          <button
            className="noti-btn primary"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            {t.notifications_mark_all}
          </button>
        </div>
      </header>

      {error && <div className="noti-alert">{error}</div>}

      {loading ? (
        <div className="noti-empty">{t.notifications_loading}</div>
      ) : filtered.length === 0 ? (
        <div className="noti-empty">
          <div className="noti-emoji">✨</div>
          <div>{t.notifications_empty}</div>
        </div>
      ) : (
        <ul className="noti-list" role="list">
          {filtered.map(n => (
            <li
              key={n.id}
              className={`noti-item ${n.read ? "is-read" : "is-unread"} level-${n.level || "info"}`}
            >
              <div className="noti-left">
                <div className="noti-icon" aria-hidden="true">
                  {n.level === "success"
                    ? "✅"
                    : n.level === "warning"
                    ? "⚠️"
                    : n.level === "error"
                    ? "⛔"
                    : "🔔"}
                </div>
              </div>

              <div className="noti-main">
                <div className="noti-top">
                  <div className="noti-title-line">
                    <strong>{n.title || t.notifications_item_no_title}</strong>
                    {!n.read && (
                      <span className="noti-dot" title={t.notifications_unread_dot} />
                    )}
                  </div>
                  <time className="noti-time" dateTime={n.created_at}>
                    {formatDateTime(n.created_at)}
                  </time>
                </div>
                <p className="noti-message">{n.message || ""}</p>

                {n.link && (
                  <a className="noti-link" href={n.link}>
                    {t.notifications_open_related}
                  </a>
                )}
              </div>

              <div className="noti-right">
                {n.read ? (
                  <button className="noti-small" onClick={() => markRead(n.id, false)}>
                    {t.notifications_mark_unread}
                  </button>
                ) : (
                  <button className="noti-small" onClick={() => markRead(n.id, true)}>
                    {t.notifications_mark_read}
                  </button>
                )}
                <button className="noti-small danger" onClick={() => remove(n.id)}>
                  {t.notifications_delete}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
